import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, DollarSign, PieChart, AlertCircle, Edit2, Check, X, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { CategoryChart } from '../components/CategoryChart';
import { TransactionList } from '../components/TransactionList';
import { statsAPI, authAPI, householdsAPI } from '../api/client';
import { useCategories } from '../hooks/useCategories';

interface DashboardState {
  stats: any | null;
  loading: boolean;
  error: string | null;
  householdId: string | null;
}

export function DashboardPage() {
  const [state, setState] = useState<DashboardState>({
    stats: null,
    loading: true,
    error: null,
    householdId: null,
  });
  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [editBudgetAmount, setEditBudgetAmount] = useState('');
  const [editBudgetFrequency, setEditBudgetFrequency] = useState('monthly');
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const { categories } = useCategories();



  // Listen for new transaction events
  useEffect(() => {
    const handleTransactionAdded = () => {
      loadDashboardData();
    };

    window.addEventListener('transactionAdded', handleTransactionAdded);
    return () => window.removeEventListener('transactionAdded', handleTransactionAdded);
  }, [selectedPeriod]);

  useEffect(() => {
    loadDashboardData();
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // First get the user and households
      const authResponse = await authAPI.getUser();
      
      if (authResponse.success && authResponse.data) {
        const { households } = authResponse.data;
        
        if (!households || households.length === 0) {
          // Create a default household if none exists
          const createResponse = await householdsAPI.createHousehold({ name: 'Personal' });

          if (createResponse.success && createResponse.data) {
            setState(prev => ({ ...prev, householdId: createResponse.data!.household.id }));
            await loadStats(createResponse.data!.household.id);
          }
        } else {
          // Use the first household
          setState(prev => ({ ...prev, householdId: households[0].id }));
          await loadStats(households[0].id);
        }
      } else {
        throw new Error('Authentication required. Please log in.');
      }
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to load dashboard',
        loading: false 
      }));
    }
  };

  const loadStats = async (householdId: string) => {
    try {
      const response = await statsAPI.getStats(householdId, selectedPeriod);
      
      if (response.success && response.data) {
        setState(prev => ({ 
          ...prev, 
          stats: response.data,
          loading: false 
        }));
      }
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to load statistics',
        loading: false 
      }));
    }
  };

  const handleUpdateBudget = async () => {
    if (!state.householdId || !editBudgetAmount) return;

    try {
      const response = await householdsAPI.updateHousehold(state.householdId, {
        income_amount: parseFloat(editBudgetAmount),
        income_frequency: editBudgetFrequency
      });

      if (response.success) {
        setIsEditingBudget(false);
        loadDashboardData(); // Reload to get updated stats
      } else {
        throw new Error(response.error || 'Failed to update budget');
      }
    } catch (error) {
      console.error('Failed to update budget:', error);
      alert('Failed to update budget');
    }
  };

  const startEditingBudget = () => {
    if (state.stats) {
      setEditBudgetAmount(state.stats.incomeAmount?.toString() || state.stats.monthlyIncome?.toString() || '0');
      setEditBudgetFrequency(state.stats.incomeFrequency || 'monthly');
      setIsEditingBudget(true);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get all transactions (both API and local)
  const getAllTransactions = () => {
    const baseTransactions = state.stats?.recentTransactions || [];
    
    // Get locally added transactions
    const localTransactions = JSON.parse(localStorage.getItem('autobudgetai-transactions') || '[]');
    
    // Combine base and local transactions
    return [...localTransactions, ...baseTransactions];
  };

  // Generate dynamic category spending data based on user's categories
  const generateDynamicCategorySpending = () => {
    const allTransactions = getAllTransactions();
    
    if (allTransactions.length === 0) {
      // If no transactions, show empty categories with user's categories
      return categories.map(category => ({
        category: category.name,
        amount: 0,
        color: category.color,
        percentage: 0,
        targetPercentage: (category as any).targetPercentage || (category as any).target_percentage || 0,
        limitAmount: (category as any).limitAmount || (category as any).limit_amount || undefined
      }));
    }

    // Calculate spending by category
    const categoryTotals = new Map<string, number>();
    let totalSpent = 0;

    allTransactions.forEach(transaction => {
      const categoryName = transaction.category;
      const current = categoryTotals.get(categoryName) || 0;
      categoryTotals.set(categoryName, current + transaction.amount);
      totalSpent += transaction.amount;
    });

    // Convert to array and include user's categories
    const spendingData = categories.map(category => {
      const amount = categoryTotals.get(category.name) || 0;
      const percentage = totalSpent > 0 ? (amount / totalSpent) * 100 : 0;
      
      return {
        category: category.name,
        amount,
        color: category.color,
        percentage,
        targetPercentage: (category as any).targetPercentage || (category as any).target_percentage || 0,
        limitAmount: (category as any).limitAmount || (category as any).limit_amount || undefined,
        transactions: allTransactions.filter(t => t.category === category.name)
      };
    });

    // Add any categories from transactions that user doesn't have
    allTransactions.forEach(transaction => {
      const userHasCategory = categories.some(cat => cat.name === transaction.category);
      if (!userHasCategory) {
        const amount = categoryTotals.get(transaction.category) || 0;
        const percentage = totalSpent > 0 ? (amount / totalSpent) * 100 : 0;
        
        // Check if we already added this category to spendingData
        const existingEntry = spendingData.find(d => d.category === transaction.category);
        if (!existingEntry) {
          spendingData.push({
            category: transaction.category,
            amount,
            color: '#6B7280', // Default gray for unknown categories
            percentage,
            targetPercentage: 0,
            limitAmount: undefined,
            transactions: allTransactions.filter(t => t.category === transaction.category)
          });
        }
      }
    });

    // Sort by amount (highest first)
    return spendingData.sort((a, b) => b.amount - a.amount);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    if (state.error.includes('Authentication required') || state.error.includes('401')) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
          <Header />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-8 shadow-sm max-w-md mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Please Log In</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">You need to be logged in to view your dashboard.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Error Loading Dashboard</h3>
            </div>
            <p className="mt-2 text-red-700 dark:text-red-300">{state.error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stats = state.stats;
  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">No data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total spent including both API and local transactions
  const allTransactions = getAllTransactions();
  const totalSpentFromLocal = allTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
  const combinedTotalSpent = (stats.totalSpent || 0) + totalSpentFromLocal;
  
  const budgetBase = stats.periodBudget || stats.monthlyBudget;
  const remainingBudget = budgetBase - combinedTotalSpent;
  const budgetUsagePercentage = (combinedTotalSpent / budgetBase) * 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notification && (
          <div className="mb-6 p-4 rounded-md bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              {notification.message}
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">Welcome back! Here's your financial overview.</p>
          </div>
          <div className="flex items-center space-x-4 w-full md:w-auto">
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="flex-1 md:flex-none px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="this-week">This Week</option>
              <option value="bi-weekly">Last 14 Days</option>
              <option value="this-month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="this-year">This Year</option>
            </select>
            <Link
              to="/add-expense"
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Spent */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center">
              <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(combinedTotalSpent)}
                </p>
              </div>
            </div>
          </div>

          {/* Monthly Budget */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
                  <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Income {stats.incomeFrequency && stats.incomeFrequency !== 'monthly' ? `(${stats.incomeFrequency})` : ''}
                  </p>
                  {isEditingBudget ? (
                    <div className="flex items-center mt-1 space-x-2">
                      <div className="flex items-center">
                        <span className="text-gray-500 dark:text-gray-400 mr-1">$</span>
                        <input
                          type="number"
                          value={editBudgetAmount}
                          onChange={(e) => setEditBudgetAmount(e.target.value)}
                          className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <select
                        value={editBudgetFrequency}
                        onChange={(e) => setEditBudgetFrequency(e.target.value)}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(stats.incomeAmount || stats.monthlyIncome)}
                      </p>
                      {stats.incomeFrequency && stats.incomeFrequency !== 'monthly' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          ~{formatCurrency(stats.monthlyBudget)}/mo
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                {isEditingBudget ? (
                  <div className="flex space-x-1">
                    <button
                      onClick={handleUpdateBudget}
                      className="p-1 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30 rounded"
                      title="Save"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setIsEditingBudget(false)}
                      className="p-1 text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700 rounded"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={startEditingBudget}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/30 rounded-full transition-colors"
                    title="Edit Income"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Budget Remaining */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${remainingBudget >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <PieChart className={`h-6 w-6 ${remainingBudget >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Remaining</p>
                <p className={`text-2xl font-bold ${remainingBudget >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(remainingBudget)}
                </p>
              </div>
            </div>
          </div>

          {/* Budget Usage */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center">
              <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-full">
                <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Budget Used</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {budgetUsagePercentage.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Category Spending Chart */}
          <div className="lg:col-span-1">
            <CategoryChart 
              categoryData={generateDynamicCategorySpending()} 
              monthlyIncome={stats.periodBudget || stats.monthlyIncome}
            />
          </div>

          {/* Recent Transactions */}
          <div className="lg:col-span-2">
            <TransactionList 
              transactions={getAllTransactions()}
              title="Recent Transactions"
              limit={6}
            />
          </div>
        </div>

        {/* Budget Progress Bar */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Budget Progress</h3>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatCurrency(combinedTotalSpent)} of {formatCurrency(stats.monthlyBudget)}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${
                budgetUsagePercentage > 90 
                  ? 'bg-red-500' 
                  : budgetUsagePercentage > 75 
                    ? 'bg-yellow-500' 
                    : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(budgetUsagePercentage, 100)}%` }}
            ></div>
          </div>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {budgetUsagePercentage > 100 
              ? 'Budget exceeded!' 
              : `You have ${formatCurrency(remainingBudget)} remaining`
            }
          </div>
        </div>
      </div>
    </div>
  );
}